/**
 * The Tasks calendar on a phone: Now, Today and Schedules.
 *
 * For the front desk checking "what's happening now" (the #2 user,
 * 2026-10-30 mockups `PhoneNow` / `PhoneToday`). It replaces the laptop's
 * Day / Week / Month at the console's phone width only; the laptop views
 * are untouched.
 *
 * It draws and fetches nothing of its own: `LaneData` is the laptop
 * lanes' (`useCalendarLaneData`), shaped for a phone by `phone-model.ts`.
 *
 * NO NEW PERMISSIONS. Every schedule action is a callback the screen
 * withholds when the viewer's role may not edit schedules (the same
 * `scheduleEditsAllowed` the laptop lanes are given); a withheld action
 * is absent, not disabled. A live task's Cancel is `PhoneTaskCancel`,
 * gated by the task drawer's own rules. While previewing another role
 * ("View as") the actions are drawn but off, and while fire mode holds
 * the site "Start now" says so instead of trying.
 *
 * TWO SCHEDULES OF ONE NAME (2026-10-30): an edit can keep a schedule's
 * old time on a second one until the change day. The Schedules tab says
 * "until Tue 03/Nov" / "from Wed 04/Nov" beside each, a task's sheet
 * finds its schedule by the fire ledger, else by the task's start
 * (`scheduleOfItem`), and Pause
 * on either one asks whether the other pauses too, Resume whether the
 * other resumes (`planPartners`, `KeptPauseConfirm`).
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { TaskState } from '../../../api/rmf-client';
import type { ScheduleOccurrence, TaskSchedule } from '../../../api/v1';
import { CATEGORY_LABEL } from '../calendar-categories';
import { formatZonedClock } from '../zoned-time';
import { DIDNT_FINISH, outcomeNote, paintOf, sourceText, type LaneItem } from './lane-items';
import { laneVars } from './lane-style';
import { tasksPhonePanelId, tasksPhoneTabId } from '../tasks-phone-tab';
import type { RobotEntry } from './lane-layout';
import {
  dateKeyOf,
  dayWindow,
  formatDuration,
  formatRange,
} from './lane-time';
import {
  buildAgenda,
  buildNowModel,
  buildScheduleRows,
  burstWords,
  groupMembership,
  isRunning,
  itemsInGroup,
  lengthText,
  NEEDS_LOOK_SHOWN,
  nextRunText,
  NEXT_SHOWN,
  phoneStatus,
  progressOf,
  robotsInGroup,
  robotText,
  runNamed,
  RUNNING_SHOWN,
  scheduleMatches,
  whatOf,
  whereOf,
  type AgendaRow,
  type CalendarIssue,
  type PhoneTab,
  type ScheduleRow,
} from './phone-model';
import { KeptPauseConfirm } from './KeptPauseConfirm';
import { useRevealOnMount } from './reveal';
import { PhoneSheet } from './PhoneSheet';
import { creatorLabel, standaloneLabel } from './people';
import {
  hasEnded,
  patternBeside,
  planPartners,
  repeatsLine,
  scheduleOfItem,
  spanInline,
  takenOverBy,
} from './schedule-span';
import type { LaneData } from './useCalendarLaneData';
import {
  FIRE_HOLD_LINE,
  PREVIEW_LINE,
  type ScheduleActionStatus,
  type ScheduleActions,
} from './useScheduleActions';
import { PriorityBadge } from '../TaskPriority';
import { isHighPriority } from '../../../api/task-priority';
import { WINDOW_KIND_WORDS } from '../../Devices/lift-rules-words';
import { describeRule } from '../rrule';
import { BlockTimeDialog, type BlockTimePreset } from './BlockTimeDialog';
import type { SiteBar } from './site-row';
import type { LiftCalendar } from './useLiftCalendar';
import './PhoneCalendar.css';

/** The schedule actions (`useScheduleActions`, shared with the laptop
 *  Agenda), under the names this file has always used. */
export type PhoneActionStatus = ScheduleActionStatus;
export type PhoneActions = ScheduleActions;

export interface PhoneCalendarProps {
  /** Block time, for a supervisor. Withheld: no button. */
  onCreateMaintenance?: (range: { startMs: number; endMs: number; reason?: string }) => void;
  liftCalendar?: Pick<LiftCalendar, 'windowOf'> &
    Partial<Pick<LiftCalendar, 'create' | 'choices' | 'state' | 'remove' | 'skip'>>;
  blockPreset?: BlockTimePreset | null;
  timeZone: string;
  nowMs: number;
  /** Today's lane data (the day window). */
  data: LaneData;
  schedules: readonly TaskSchedule[];
  /** Today's and tomorrow's occurrences, for each schedule's next run. */
  occurrences: readonly ScheduleOccurrence[];
  issues: readonly CalendarIssue[];
  /** Robot keys the live list says cannot take work. */
  unavailable: ReadonlySet<string>;
  tab: PhoneTab;
  onTab: (tab: PhoneTab) => void;
  sheet: string | null;
  onOpenSheet: (id: string) => void;
  onCloseSheet: () => void;
  actions: PhoneActions;
  onOpenTask?: (item: LaneItem) => void;
  renderTaskCancel?: (task: TaskState) => ReactNode;
  /** Present when this viewer may open the Alerts screen. */
  onOpenAlerts?: () => void;
  /** The occurrence reads failed: scheduled work may be missing. */
  scheduleReadFailed?: boolean;
  /**
   * The screen's tab bar (`TasksPhoneTabs`: Table · Now · Today ·
   * Schedules), drawn at the top of the calendar's sticky head with the
   * group chips under it. The calendar no longer draws a switch of its
   * own: the screen's one bar picks the tab, and `tab` / `onTab` say
   * which it is.
   */
  tabBar?: ReactNode;
  /** The screen's banners, drawn under the sticky head, above the tab. */
  notices?: ReactNode;
  /** The signed-in user's subject: a schedule they set up is "You". */
  mySub?: string | null;
}


function terminal(t: TaskState): boolean {
  return t.status === 'completed' || t.status === 'failed' || t.status === 'cancelled';
}

export function PhoneCalendar(props: PhoneCalendarProps) {
  const { data, nowMs, timeZone } = props;
  const [groupId, setGroupId] = useState<string | null>(null);
  const [showEarlier, setShowEarlier] = useState(false);
  const [showAllRunning, setShowAllRunning] = useState(false);
  const [query, setQuery] = useState('');
  // Local, not in the URL: Back closes a sheet, and this one has typing in it.
  const [blocking, setBlocking] = useState(false);
  const canBlock = Boolean(props.onCreateMaintenance || props.liftCalendar?.create);
  useEffect(() => {
    if (props.blockPreset && canBlock) setBlocking(true);
  }, [props.blockPreset, canBlock]);

  const labels = useMemo(() => new Map(data.robots.map((r) => [r.key, r.label])), [data.robots]);
  const labelOf = (key: string | null) => (key ? (labels.get(key) ?? null) : null);

  const membership = useMemo(
    () => groupMembership(data.items, data.groups, data.robots),
    [data.items, data.groups, data.robots],
  );
  // A chip whose group is gone (groups edited, site switched) reads as All.
  const activeGroup = groupId && membership.chips.some((c) => c.id === groupId) ? groupId : null;
  const items = useMemo(
    () => itemsInGroup(data.items, membership, activeGroup),
    [data.items, membership, activeGroup],
  );
  const todayKey = dateKeyOf(nowMs, timeZone);

  const byId = useMemo(() => new Map(data.items.map((it) => [it.id, it])), [data.items]);
  const scheduleRows = useMemo(
    () =>
      buildScheduleRows({
        schedules: props.schedules,
        occurrences: props.occurrences,
        nowMs,
        timeZone,
        labelOf,
      }),
    // labelOf follows `labels`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.schedules, props.occurrences, nowMs, timeZone, labels],
  );

  return (
    <section className="nx-pcal" aria-label="Calendar" data-testid="pcal">
      {/* Sticky under the shell's top bar: the tab bar and the chips stay
          in reach however far down the list is read (`AppShell.css`,
          `use-scroll-tuck.ts`). */}
      <div className="nx-pcal__head" data-testid="pcal-head">
        {props.tabBar}
        {props.tab !== 'schedules' && membership.chips.length > 1 && (
          <div className="nx-pcal__chips" role="group" aria-label="Robot group">
            <Chip on={activeGroup === null} onClick={() => setGroupId(null)} testId="pcal-chip-all">
              All
            </Chip>
            {membership.chips.map((c) => (
              <Chip
                key={c.id}
                on={activeGroup === c.id}
                onClick={() => setGroupId(c.id)}
                testId={`pcal-chip-${c.id}`}
              >
                {c.name}
              </Chip>
            ))}
          </div>
        )}
      </div>

      {props.notices}

      {props.scheduleReadFailed && (
        <p className="nx-pcal-note is-fail nx-pcal__banner" role="alert">
          Scheduled tasks could not be loaded. Work still to come may be missing below.
        </p>
      )}

      <div
        className="nx-pcal__panel"
        role="tabpanel"
        id={tasksPhonePanelId(props.tab)}
        aria-labelledby={tasksPhoneTabId(props.tab)}
      >
        {props.tab === 'now' && (
          <NowTab
            {...props}
            items={items}
            robotsInView={robotsInGroup(data.robots, membership, activeGroup)}
            issues={props.issues.filter((i) => activeGroup === null || i.groupId === activeGroup)}
            labelOf={labelOf}
            showAllRunning={showAllRunning}
            onShowAllRunning={() => setShowAllRunning(true)}
            onSeeToday={(earlier) => {
              if (earlier) setShowEarlier(true);
              props.onTab('today');
            }}
          />
        )}
        {props.tab === 'today' && (
          <TodayTab
            items={items}
            nowMs={nowMs}
            timeZone={timeZone}
            labelOf={labelOf}
            showEarlier={showEarlier}
            onToggleEarlier={() => setShowEarlier((v) => !v)}
            onOpen={props.onOpenSheet}
            siteBars={props.liftCalendar ? data.siteBars : []}
            onBlockTime={canBlock ? () => setBlocking(true) : undefined}
          />
        )}
        {props.tab === 'schedules' && (
          <SchedulesTab
            rows={scheduleRows}
            query={query}
            onQuery={setQuery}
            nowMs={nowMs}
            timeZone={timeZone}
            onOpen={props.onOpenSheet}
          />
        )}
      </div>

      {blocking && canBlock && (
        <PhoneSheet title="Block time" onClose={() => setBlocking(false)} testId="pcal-block-sheet">
          <BlockTimeDialog
            bare
            day={todayKey}
            timeZone={timeZone}
            defaultFrom={`${String((Number(formatZonedClock(nowMs, timeZone).slice(0, 2)) + 1) % 24).padStart(2, '0')}:00`}
            canHold={Boolean(props.onCreateMaintenance)}
            lifts={props.liftCalendar?.create ? (props.liftCalendar as LiftCalendar) : undefined}
            preset={props.blockPreset}
            onCancel={() => setBlocking(false)}
            onConfirm={(range) => {
              props.onCreateMaintenance?.(range);
              setBlocking(false);
            }}
          />
        </PhoneSheet>
      )}

      {props.sheet && (
        <SheetFor
          {...props}
          id={props.sheet}
          byId={byId}
          rows={scheduleRows}
          labelOf={labelOf}
          todayKey={todayKey}
        />
      )}
    </section>
  );
}

function Chip({
  on,
  onClick,
  children,
  testId,
}: {
  on: boolean;
  onClick: () => void;
  children: ReactNode;
  testId: string;
}) {
  return (
    <button
      type="button"
      className={`nx-pcal__chip${on ? ' is-on' : ''}`}
      aria-pressed={on}
      onClick={onClick}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

function TypePill({ item, outline }: { item: Pick<LaneItem, 'lane' | 'paint' | 'typeLabel'>; outline?: boolean }) {
  return (
    <span
      className={`nx-pcal-pill${outline ? ' is-outline' : ''}`}
      style={laneVars(paintOf(item))}
    >
      {item.typeLabel}
    </span>
  );
}

// ---- Now ------------------------------------------------------------------------

function NowTab(
  props: PhoneCalendarProps & {
    items: LaneItem[];
    robotsInView: RobotEntry[];
    issues: readonly CalendarIssue[];
    labelOf: (key: string | null) => string | null;
    showAllRunning: boolean;
    onShowAllRunning: () => void;
    onSeeToday: (earlier: boolean) => void;
  },
) {
  const { nowMs, timeZone, labelOf } = props;
  const todayKey = dateKeyOf(nowMs, timeZone);
  const model = buildNowModel({
    items: props.items,
    robots: props.robotsInView,
    unavailable: props.unavailable,
    nowMs,
    day: dayWindow(todayKey, timeZone),
  });
  // Robot problems first: they are happening now, and somebody can go
  // and fix one; a job that didn't finish is already over.
  const looks = [
    ...props.issues.map((issue) => ({ kind: 'issue' as const, issue })),
    ...model.didntFinish.map((it) => ({ kind: 'task' as const, it })),
  ];
  const shownLooks = looks.slice(0, NEEDS_LOOK_SHOWN);
  const running = props.showAllRunning ? model.running : model.running.slice(0, RUNNING_SHOWN);
  const nextShown = model.next.slice(0, NEXT_SHOWN);

  return (
    <div className="nx-pcal-now" data-testid="pcal-now">
      {looks.length > 0 && (
        <section className="nx-pcal-look" aria-labelledby="pcal-look-h" data-testid="pcal-look">
          <h3 id="pcal-look-h" className="nx-pcal-look__h">
            Needs a look · {looks.length}
          </h3>
          <ul className="nx-pcal-look__list">
            {shownLooks.map((l) =>
              l.kind === 'task' ? (
                <li key={l.it.id}>
                  <button
                    type="button"
                    className="nx-pcal-look__row"
                    onClick={() => props.onOpenSheet(l.it.id)}
                    data-testid={`pcal-look-task-${l.it.id}`}
                  >
                    <span>
                      <b>{robotText(l.it, labelOf)}</b> · {whatOf(l.it)}{' '}
                      {l.it.outcome === 'cancelled' ? 'was cancelled' : 'didn’t finish'}
                    </span>
                    <span className="nx-pcal-look__time">
                      {formatZonedClock(l.it.endMs, timeZone)}
                    </span>
                  </button>
                </li>
              ) : (
                <li key={l.issue.id} className="nx-pcal-look__row is-static" data-testid={`pcal-look-issue-${l.issue.id}`}>
                  <span>
                    <b>{l.issue.subject}</b> · {l.issue.words}
                  </span>
                  {l.issue.sinceMs !== null && (
                    <span className="nx-pcal-look__time">
                      {formatZonedClock(l.issue.sinceMs, timeZone)}
                    </span>
                  )}
                </li>
              ),
            )}
          </ul>
          <div className="nx-pcal-look__more">
            {looks.length > shownLooks.length && (
              <button
                type="button"
                className="nx-pcal-link"
                onClick={() => props.onSeeToday(true)}
                data-testid="pcal-look-more"
              >
                + {looks.length - shownLooks.length} more
              </button>
            )}
            {props.issues.length > 0 && props.onOpenAlerts && (
              <button type="button" className="nx-pcal-link" onClick={props.onOpenAlerts}>
                Open Alerts
              </button>
            )}
          </div>
        </section>
      )}

      <div className="nx-pcal-now__bar">
        <h3 className="nx-pcal-h">Running now · {model.running.length}</h3>
        <span className="nx-pcal-muted" data-testid="pcal-free">
          {model.freeRobots} {model.freeRobots === 1 ? 'robot' : 'robots'} free
        </span>
      </div>
      {model.running.length === 0 ? (
        <p className="nx-pcal-empty">Nothing on the calendar is running right now.</p>
      ) : (
        <ul className="nx-pcal-cards">
          {running.map((it) => (
            <li key={it.id}>
              <RunningCard item={it} nowMs={nowMs} timeZone={timeZone} labelOf={labelOf} onOpen={props.onOpenSheet} />
            </li>
          ))}
        </ul>
      )}
      {model.running.length > running.length && (
        <button
          type="button"
          className="nx-pcal-link"
          onClick={props.onShowAllRunning}
          data-testid="pcal-running-more"
        >
          + {model.running.length - running.length} more running
        </button>
      )}

      <h3 className="nx-pcal-h">Next 30 minutes · {model.next.length}</h3>
      {model.next.length === 0 ? (
        <p className="nx-pcal-empty">Nothing starts in the next 30 minutes.</p>
      ) : (
        <ul className="nx-pcal-next">
          {nextShown.map((it) => (
            <li key={it.id}>
              <button
                type="button"
                className="nx-pcal-next__row"
                onClick={() => props.onOpenSheet(it.id)}
                data-testid={`pcal-next-${it.id}`}
              >
                <span className="nx-pcal-next__t">{formatZonedClock(it.startMs, timeZone)}</span>
                <span className="nx-pcal-swatch" style={laneVars(paintOf(it))} aria-hidden="true" />
                {it.high && <PriorityBadge testId={`pcal-next-high-${it.id}`} />}
                <span className="nx-pcal-next__what">{whatOf(it)}</span>
                <span className="nx-pcal-muted">{robotText(it, labelOf)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {model.next.length > nextShown.length && (
        <button type="button" className="nx-pcal-link" onClick={() => props.onSeeToday(false)}>
          + {model.next.length - nextShown.length} more — see Today
        </button>
      )}
    </div>
  );
}

function RunningCard({
  item,
  nowMs,
  timeZone,
  labelOf,
  onOpen,
}: {
  item: LaneItem;
  nowMs: number;
  timeZone: string;
  labelOf: (key: string | null) => string | null;
  onOpen: (id: string) => void;
}) {
  const pct = progressOf(item, nowMs);
  return (
    <button
      type="button"
      className={`nx-pcal-card${item.outcome === 'attention' ? ' is-attention' : ''}`}
      style={laneVars(paintOf(item))}
      onClick={() => onOpen(item.id)}
      data-testid={`pcal-running-${item.id}`}
    >
      <span className="nx-pcal-card__top">
        <span className="nx-pcal-card__robot">{robotText(item, labelOf)}</span>
        <span className="nx-pcal-card__pills">
          {item.high && <PriorityBadge testId={`pcal-running-high-${item.id}`} />}
          <TypePill item={item} />
        </span>
      </span>
      <span className="nx-pcal-card__mid">
        <span>{whereOf(item) ?? 'Place not known yet'}</span>
        <span>
          {pct !== null
            ? `ends ~${formatZonedClock(item.endMs, timeZone)}`
            : `started ${formatZonedClock(item.startMs, timeZone)}`}
        </span>
      </span>
      {item.outcome === 'attention' && (
        <span className="nx-pcal-card__warn">Needs a look{item.detail ? `: ${item.detail}` : ''}</span>
      )}
      {pct !== null && (
        <span
          className="nx-pcal-progress"
          role="progressbar"
          aria-label="Progress (estimate)"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
        >
          <span className="nx-pcal-progress__fill" style={{ width: `${pct}%` }} />
        </span>
      )}
    </button>
  );
}

// ---- Today ---------------------------------------------------------------------

/** What holds the site or a lift today, and Block time for a supervisor. The
 *  laptop's Site row, as a list. Fire stays with the alarm's own surfaces. */
function PhoneSiteToday(props: {
  bars: readonly SiteBar[];
  timeZone: string;
  nowMs: number;
  onOpen: (id: string) => void;
  onBlockTime?: () => void;
}) {
  const bars = props.bars.filter((b) => b.kind !== 'fire' && b.endMs > props.nowMs);
  if (bars.length === 0 && !props.onBlockTime) return null;
  return (
    <section className="nx-pcal-site" aria-label="Today at the site" data-testid="pcal-site">
      {bars.length > 0 && (
        <ul className="nx-pcal-site__list">
          {bars.map((b) => {
            const when = formatRange(b.startMs, b.endMs, props.timeZone);
            return (
              <li key={b.id}>
                {b.liftRule ? (
                  <button
                    type="button"
                    className={`nx-pcal-site__row${b.liftRule.kind === 'allow_travel' ? ' is-allow' : ''}`}
                    onClick={() => props.onOpen(b.id)}
                    data-testid={`pcal-site-${b.id}`}
                  >
                    <strong>{b.label}</strong>
                    <span>{when}</span>
                  </button>
                ) : (
                  <span className="nx-pcal-site__row" data-testid={`pcal-site-${b.id}`}>
                    <strong>{b.label}</strong>
                    <span>{when}</span>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {props.onBlockTime && (
        <button
          type="button"
          className="nx-pcal-btn"
          onClick={props.onBlockTime}
          data-testid="pcal-block-time"
        >
          Block time
        </button>
      )}
    </section>
  );
}

function TodayTab(props: {
  siteBars: readonly SiteBar[];
  onBlockTime?: () => void;
  items: LaneItem[];
  nowMs: number;
  timeZone: string;
  labelOf: (key: string | null) => string | null;
  showEarlier: boolean;
  onToggleEarlier: () => void;
  onOpen: (id: string) => void;
}) {
  const { nowMs, timeZone } = props;
  const agenda = useMemo(() => buildAgenda(props.items, nowMs), [props.items, nowMs]);
  const c = agenda.earlierCount;
  const earlierParts = [
    `${c.done} done`,
    ...(c.didntFinish ? [`${c.didntFinish} didn’t finish`] : []),
    ...(c.other ? [`${c.other} other`] : []),
  ];
  const nowLine = (
    <li className="nx-pcal-nowline" aria-label={`Now, ${formatZonedClock(nowMs, timeZone)}`} data-testid="pcal-nowline">
      <span>NOW {formatZonedClock(nowMs, timeZone)}</span>
      <span className="nx-pcal-nowline__rule" aria-hidden="true" />
    </li>
  );
  const empty = agenda.rows.length === 0 && agenda.earlier.length === 0;
  return (
    <div className="nx-pcal-today" data-testid="pcal-today">
      <PhoneSiteToday
        bars={props.siteBars}
        timeZone={timeZone}
        nowMs={nowMs}
        onOpen={props.onOpen}
        onBlockTime={props.onBlockTime}
      />
      {agenda.earlier.length > 0 && (
        <div className="nx-pcal-earlier">
          <span>Earlier today: {earlierParts.join(' · ')}</span>
          <button
            type="button"
            className="nx-pcal-link"
            aria-expanded={props.showEarlier}
            onClick={props.onToggleEarlier}
            data-testid="pcal-earlier-toggle"
          >
            {props.showEarlier ? 'hide' : 'show'}
          </button>
        </div>
      )}
      {empty ? (
        <p className="nx-pcal-empty">Nothing on the calendar today.</p>
      ) : (
        <ul className="nx-pcal-agenda" aria-label="Today">
          {props.showEarlier &&
            agenda.earlier.map((r) => (
              <AgendaRowView key={r.id} row={r} timeZone={timeZone} labelOf={props.labelOf} onOpen={props.onOpen} />
            ))}
          {agenda.rows.map((r, i) => (
            <AgendaFragment
              key={r.id}
              before={i === agenda.nowIndex ? nowLine : null}
              row={r}
              timeZone={timeZone}
              labelOf={props.labelOf}
              onOpen={props.onOpen}
            />
          ))}
          {agenda.nowIndex === agenda.rows.length && nowLine}
        </ul>
      )}
    </div>
  );
}

function AgendaFragment(props: {
  before: ReactNode;
  row: AgendaRow;
  timeZone: string;
  labelOf: (key: string | null) => string | null;
  onOpen: (id: string) => void;
}) {
  return (
    <>
      {props.before}
      <AgendaRowView row={props.row} timeZone={props.timeZone} labelOf={props.labelOf} onOpen={props.onOpen} />
    </>
  );
}

function statusClass(item: LaneItem): string {
  if (DIDNT_FINISH.has(item.outcome)) return ' is-fail';
  if (isRunning(item.outcome)) return ' is-run';
  return '';
}

function BurstStatus({ w }: { w: ReturnType<typeof burstWords> }) {
  if (w.allFailed) return <span className="nx-pcal-status is-fail">{w.status}</span>;
  return (
    <>
      <span className="nx-pcal-status">{w.status}</span>
      {w.didntFinish > 0 && (
        <>
          {' · '}
          <span className="nx-pcal-status is-fail">{w.didntFinish} didn’t finish</span>
        </>
      )}
    </>
  );
}

function AgendaRowView({
  row,
  timeZone,
  labelOf,
  onOpen,
}: {
  row: AgendaRow;
  timeZone: string;
  labelOf: (key: string | null) => string | null;
  onOpen: (id: string) => void;
}) {
  if (row.kind === 'burst') {
    const w = burstWords(row.items);
    const first = row.items[0]!;
    return (
      <li>
        <button
          type="button"
          className={`nx-pcal-row${w.didntFinish ? ' is-fail' : ''}`}
          onClick={() => onOpen(row.id)}
          data-testid={`pcal-row-${row.id}`}
        >
          <span className="nx-pcal-row__when">
            <span className="nx-pcal-row__t">{formatZonedClock(row.startMs, timeZone)}</span>
            <span className="nx-pcal-row__len">{w.length}</span>
          </span>
          <span className="nx-pcal-row__main">
            <span className="nx-pcal-row__line">
              <TypePill item={first} outline={first.outcome === 'scheduled' || first.outcome === 'queued'} />
              <span className="nx-pcal-row__where">{w.where}</span>
            </span>
            <span className="nx-pcal-row__sub">
              {robotText(first, labelOf)} · {row.items.length} deliveries · <BurstStatus w={w} />
            </span>
          </span>
        </button>
      </li>
    );
  }
  const it = row.item;
  const fail = DIDNT_FINISH.has(it.outcome);
  return (
    <li>
      <button
        type="button"
        className={`nx-pcal-row${fail ? ' is-fail' : ''}${it.outcome === 'done' ? ' is-done' : ''}`}
        onClick={() => onOpen(it.id)}
        data-testid={`pcal-row-${it.id}`}
        data-outcome={it.outcome}
      >
        <span className="nx-pcal-row__when">
          <span className="nx-pcal-row__t">{formatZonedClock(it.startMs, timeZone)}</span>
          <span className="nx-pcal-row__len">{lengthText(it)}</span>
        </span>
        <span className="nx-pcal-row__main">
          <span className="nx-pcal-row__line">
            <TypePill item={it} outline={it.outcome === 'scheduled' || it.outcome === 'queued'} />
            {it.high && <PriorityBadge testId={`pcal-row-high-${it.id}`} />}
            <span className={`nx-pcal-row__where${whereOf(it) ? '' : ' is-unknown'}`}>
              {whereOf(it) ?? 'Place not known yet'}
            </span>
          </span>
          <span className="nx-pcal-row__sub">
            {robotText(it, labelOf)} ·{' '}
            <span className={`nx-pcal-status${statusClass(it)}`}>{phoneStatus(it.outcome)}</span>
          </span>
        </span>
      </button>
    </li>
  );
}

// ---- Schedules -----------------------------------------------------------------

function SchedulesTab(props: {
  rows: ScheduleRow[];
  query: string;
  onQuery: (q: string) => void;
  nowMs: number;
  timeZone: string;
  onOpen: (id: string) => void;
}) {
  const shown = props.rows.filter((r) => scheduleMatches(r, props.query));
  return (
    <div className="nx-pcal-sched" data-testid="pcal-schedules">
      <label className="nx-pcal-search">
        <span className="nx-pcal-search__label">Search schedules</span>
        <input
          type="search"
          value={props.query}
          onChange={(e) => props.onQuery(e.target.value)}
          placeholder="Name, place or robot"
          data-testid="pcal-sched-search"
        />
      </label>
      <h3 className="nx-pcal-h">
        Repeating schedules · {props.query.trim() ? `${shown.length} of ${props.rows.length}` : props.rows.length}
      </h3>
      {shown.length === 0 ? (
        <p className="nx-pcal-empty">
          {props.rows.length === 0 ? 'No repeating schedules yet.' : 'No schedule matches that search.'}
        </p>
      ) : (
        <ul className="nx-pcal-sched__list">
          {shown.map((r) => (
            <li key={r.schedule.schedule_id}>
              <button
                type="button"
                className="nx-pcal-sched__row"
                onClick={() => props.onOpen(`schedule:${r.schedule.schedule_id}`)}
                data-testid={`pcal-sched-${r.schedule.schedule_id}`}
              >
                <span className="nx-pcal-sched__top">
                  <span className="nx-pcal-sched__name">
                    <span className="nx-pcal-swatch is-solid" style={laneVars(r.lane)} aria-hidden="true" />
                    {r.schedule.name}
                    {isHighPriority(r.schedule.task?.priority) && (
                      <>
                        {' '}
                        <PriorityBadge testId={`pcal-sched-high-${r.schedule.schedule_id}`} />
                      </>
                    )}
                  </span>
                  <span className={`nx-pcal-state${r.active ? ' is-on' : ' is-paused'}`}>
                    {r.active ? 'Active' : 'Paused'}
                  </span>
                </span>
                <span className="nx-pcal-muted">
                  {patternBeside(r.pattern, r.span)}
                  {r.span && (
                    <>
                      ,{' '}
                      <span
                        className="nx-pcal-sched__span"
                        data-testid={`pcal-sched-span-${r.schedule.schedule_id}`}
                      >
                        {spanInline(r.span)}
                      </span>
                    </>
                  )}{' '}
                  · {r.robots}
                </span>
                <span className="nx-pcal-sched__next">
                  {r.active
                    ? r.next
                      ? `Next: ${nextRunText(r.next.start_ms, props.nowMs, props.timeZone)}`
                      : 'Next: not in the next two days'
                    : 'Paused: nothing will run'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---- the sheet ------------------------------------------------------------------

function SheetFor(
  props: PhoneCalendarProps & {
    id: string;
    byId: ReadonlyMap<string, LaneItem>;
    rows: ScheduleRow[];
    labelOf: (key: string | null) => string | null;
    todayKey: string;
  },
) {
  const { id, timeZone } = props;
  if (id.startsWith('lift:')) {
    const bar = props.data.siteBars.find((b) => b.id === id);
    if (!bar?.liftRule) return <GoneSheet onClose={props.onCloseSheet} />;
    return <LiftRuleSheet {...props} bar={bar} />;
  }
  if (id.startsWith('schedule:')) {
    const row = props.rows.find((r) => r.schedule.schedule_id === id.slice('schedule:'.length));
    if (!row) return <GoneSheet onClose={props.onCloseSheet} />;
    return <ScheduleSheet {...props} row={row} />;
  }
  if (id.startsWith('burst:')) {
    const first = props.byId.get(id.slice('burst:'.length));
    const rows = first ? buildAgenda([...props.byId.values()], props.nowMs) : null;
    const burst = rows
      ? [...rows.earlier, ...rows.rows].find((r) => r.kind === 'burst' && r.id === id)
      : undefined;
    if (!burst || burst.kind !== 'burst') return <GoneSheet onClose={props.onCloseSheet} />;
    const w = burstWords(burst.items);
    return (
      <PhoneSheet
        title={`${burst.items.length} deliveries`}
        eyebrow={robotText(burst.items[0]!, props.labelOf)}
        onClose={props.onCloseSheet}
      >
        <dl className="nx-pcal-kv">
          <Kv k="When" v={formatRange(burst.startMs, burst.endMs, timeZone)} />
          {w.length && <Kv k="Each" v={w.length} />}
          <Kv k="Status" v={<BurstStatus w={w} />} />
        </dl>
        <ul className="nx-pcal-burst">
          {burst.items.map((it) => (
            <li key={it.id}>
              <button
                type="button"
                className="nx-pcal-next__row"
                onClick={() => props.onOpenSheet(it.id)}
                data-testid={`pcal-burst-${it.id}`}
              >
                <span className="nx-pcal-next__t">{formatZonedClock(it.startMs, timeZone)}</span>
                <span className="nx-pcal-next__what">{whereOf(it) ?? it.typeLabel}</span>
                <span className={`nx-pcal-status${statusClass(it)}`}>{phoneStatus(it.outcome)}</span>
              </button>
            </li>
          ))}
        </ul>
      </PhoneSheet>
    );
  }
  const item = props.byId.get(id);
  if (!item) return <GoneSheet onClose={props.onCloseSheet} />;
  return <ItemSheet {...props} item={item} />;
}

/** A run of a lift rule: what it does, and Skip or Remove behind Keep. */
function LiftRuleSheet(props: PhoneCalendarProps & { bar: SiteBar }) {
  const { bar, timeZone } = props;
  const run = bar.liftRule!;
  const win = props.liftCalendar?.windowOf(run.window_id) ?? null;
  const repeats = Boolean(win?.rrule);
  const [asking, setAsking] = useState<'skip' | 'remove' | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const { remove, skip } = props.liftCalendar ?? {};
  const act = (answer: Promise<string | null>) => {
    setAsking(null);
    setRefusal(null);
    void answer.then((why) => {
      if (why) setRefusal(why);
      else props.onCloseSheet();
    });
  };
  return (
    <PhoneSheet title={WINDOW_KIND_WORDS[run.kind]} eyebrow={run.lift} onClose={props.onCloseSheet}>
      <dl className="nx-pcal-kv">
        <Kv k="When" v={formatRange(bar.startMs, bar.endMs, timeZone)} />
        {win?.rrule && (
          <Kv
            k="Repeats"
            v={describeRule(win.rrule, win.starts_at_ms, win.repeat_until_ms, win.timezone, {
              siteTimeZone: timeZone,
            })}
          />
        )}
        {run.reason && <Kv k="Reason" v={run.reason} />}
        {win?.created_by_email && <Kv k="Set up by" v={win.created_by_email} />}
      </dl>
      <p className="nx-pcal-note">
        {run.kind === 'block_use'
          ? 'Robots do not use this lift during this time. A robot already riding finishes its ride first.'
          : 'Robots may ride this lift between floors during this time.'}
      </p>
      {refusal && (
        <p className="nx-pcal-note is-hold" role="alert">
          {refusal}
        </p>
      )}
      {asking ? (
        <div className="nx-pcal-actions" role="group" aria-label="Confirm" data-testid="pcal-lift-confirm">
          <button type="button" className="nx-pcal-btn" onClick={() => setAsking(null)}>
            Keep
          </button>
          <button
            type="button"
            className="nx-pcal-btn nx-pcal-btn--danger"
            data-testid="pcal-lift-confirm-go"
            onClick={() => {
              if (asking === 'skip') act(skip!(run.window_id, run.starts_at_ms));
              else act(remove!(run.window_id));
            }}
          >
            {asking === 'skip' ? 'Skip it' : 'Remove it'}
          </button>
        </div>
      ) : (
        (remove || skip) && (
          <div className="nx-pcal-actions">
            {repeats && skip && (
              <button
                type="button"
                className="nx-pcal-btn"
                onClick={() => setAsking('skip')}
                data-testid="pcal-lift-skip"
              >
                Skip this one
              </button>
            )}
            {remove && (
              <button
                type="button"
                className="nx-pcal-btn"
                onClick={() => setAsking('remove')}
                data-testid="pcal-lift-remove"
              >
                {repeats ? 'Remove every run' : 'Remove from the calendar'}
              </button>
            )}
          </div>
        )
      )}
    </PhoneSheet>
  );
}

function GoneSheet({ onClose }: { onClose: () => void }) {
  return (
    <PhoneSheet title="Not on today’s calendar" onClose={onClose}>
      <p className="nx-pcal-note">This is no longer on today’s calendar.</p>
    </PhoneSheet>
  );
}

function Kv({ k, v, fail }: { k: string; v: ReactNode; fail?: boolean }) {
  return (
    <div className="nx-pcal-kv__row">
      <dt>{k}</dt>
      <dd className={fail ? 'is-fail' : undefined}>{v}</dd>
    </div>
  );
}

function ActionStatus({ status }: { status: PhoneActionStatus | null }) {
  if (!status || (!status.message && !status.pending)) return null;
  if (status.pending) {
    return (
      <p className="nx-pcal-note" role="status">
        Sending…
      </p>
    );
  }
  return (
    <p
      className={`nx-pcal-note${status.tone === 'ok' ? ' is-ok' : status.tone === 'error' ? ' is-fail' : ' is-hold'}`}
      role={status.tone === 'ok' ? 'status' : 'alert'}
      data-testid="pcal-action-status"
    >
      {status.message}
    </p>
  );
}

function OccurrenceActions({
  occurrence,
  actions,
  nowMs,
  timeZone,
}: {
  occurrence: ScheduleOccurrence;
  actions: PhoneActions;
  nowMs: number;
  timeZone: string;
}) {
  const [confirmSkip, setConfirmSkip] = useState(false);
  const off = actions.actionsOff || Boolean(actions.status?.pending);
  if (!actions.startNow && !actions.skip) return null;
  return (
    <div className="nx-pcal-actions" data-testid="pcal-occ-actions">
      {actions.startNow && (
        <button
          type="button"
          className="nx-pcal-btn nx-pcal-btn--primary"
          disabled={off || actions.fireHold}
          title={actions.actionsOff ? PREVIEW_LINE : actions.fireHold ? FIRE_HOLD_LINE : 'Send it to the fleet now instead of at its time.'}
          onClick={() => actions.startNow!(occurrence)}
          data-testid="pcal-start-now"
        >
          Start now
        </button>
      )}
      {actions.skip && !confirmSkip && (
        <button
          type="button"
          className="nx-pcal-btn"
          disabled={off}
          title={actions.actionsOff ? PREVIEW_LINE : 'Skip just this time; the schedule stays.'}
          onClick={() => setConfirmSkip(true)}
          data-testid="pcal-skip"
        >
          Skip this time
        </button>
      )}
      {actions.skip && confirmSkip && (
        <PhoneSkipConfirm
          run={runNamed(occurrence.start_ms, nowMs, timeZone)}
          disabled={off}
          onKeep={() => setConfirmSkip(false)}
          onSkip={() => {
            setConfirmSkip(false);
            actions.skip!(occurrence);
          }}
        />
      )}
      {actions.startNow && actions.fireHold && (
        <p className="nx-pcal-note is-hold" data-testid="pcal-fire-hold">
          {FIRE_HOLD_LINE}
        </p>
      )}
    </div>
  );
}

/** "Skip today's 09:00 run?" in place of the button that asked: brought
 *  into view in the sheet and focused on "Keep it" (`useRevealOnMount`),
 *  as the laptop panel's is. The run is named as the laptop names it
 *  (`runNamed`): the phone said "Skip the 15:00 run?" where the Agenda
 *  said "Skip today's 14:00 run?" (review BC-F16, 2026-10-31). */
function PhoneSkipConfirm(props: {
  run: string;
  disabled: boolean;
  onKeep: () => void;
  onSkip: () => void;
}) {
  const ref = useRevealOnMount<HTMLDivElement>();
  return (
    <div ref={ref} className="nx-pcal-confirm" role="group" aria-label="Confirm skip">
      <p className="nx-pcal-confirm__q">
        Skip {props.run}? Only this time is skipped; the schedule keeps running.
      </p>
      <div className="nx-pcal-confirm__row">
        <button type="button" className="nx-pcal-btn" onClick={props.onKeep}>
          Keep it
        </button>
        <button
          type="button"
          className="nx-pcal-btn nx-pcal-btn--danger"
          disabled={props.disabled}
          onClick={props.onSkip}
          data-testid="pcal-skip-confirm"
        >
          Skip this time
        </button>
      </div>
    </div>
  );
}

function PauseButton({
  schedule,
  schedules,
  actions,
  nowMs,
  timeZone,
}: {
  schedule: TaskSchedule;
  schedules: readonly TaskSchedule[];
  actions: PhoneActions;
  nowMs: number;
  timeZone: string;
}) {
  const [confirming, setConfirming] = useState(false);
  if (!actions.setEnabled) return null;
  const off = actions.actionsOff || Boolean(actions.status?.pending);
  // The other rows of its plan in the same state (an edit's old time
  // kept, or the new time after it): Pause or Resume asks first.
  const partners = planPartners(schedule, schedules, nowMs, timeZone);
  if (confirming && partners) {
    return (
      <KeptPauseConfirm
        text={partners.text}
        // A phone line each, kept short: a daily row by its time.
        list={partners.listShort}
        count={partners.rows.length}
        verb={partners.verb}
        disabled={off}
        onBoth={() => {
          setConfirming(false);
          actions.setEnabled!(
            schedule.schedule_id,
            !schedule.enabled,
            partners.rows.map((k) => k.schedule_id),
          );
        }}
        onOne={() => {
          setConfirming(false);
          actions.setEnabled!(schedule.schedule_id, !schedule.enabled);
        }}
        onCancel={() => setConfirming(false)}
        className="nx-pcal-confirm"
        noteClass="nx-pcal-note"
        actionsClass="nx-pcal-actions"
        btnClass="nx-pcal-btn"
        testId="pcal-pause-confirm"
      />
    );
  }
  return (
    <button
      type="button"
      className="nx-pcal-btn"
      disabled={off}
      title={
        actions.actionsOff
          ? PREVIEW_LINE
          : schedule.enabled
            ? 'Stop every future run. Its times and history are kept.'
            : 'Start running this schedule again from its next time.'
      }
      onClick={() =>
        partners ? setConfirming(true) : actions.setEnabled!(schedule.schedule_id, !schedule.enabled)
      }
      data-testid={schedule.enabled ? 'pcal-pause' : 'pcal-resume'}
    >
      {schedule.enabled ? 'Pause schedule' : 'Resume schedule'}
    </button>
  );
}

function ItemSheet(
  props: PhoneCalendarProps & {
    item: LaneItem;
    labelOf: (key: string | null) => string | null;
  },
) {
  const { item, timeZone, actions } = props;
  // The schedule behind it: an occurrence names it; a task it fired is
  // the fire ledger's, else its requester's name (`calendar:<name>`)
  // read against the task's start, as the lanes' panel reads it
  // (`scheduleOfItem`).
  const schedule = scheduleOfItem(item, props.schedules, props.nowMs);
  // A run of a schedule that has ended (a carry-over past its change
  // day): Pause is the one that took over's, or none.
  const pauseTarget =
    schedule && hasEnded(schedule, props.nowMs)
      ? takenOverBy(schedule, props.schedules, props.nowMs)
      : schedule;
  const fail = DIDNT_FINISH.has(item.outcome);
  const plan = item.kind === 'occurrence' && item.outcome === 'scheduled';
  const note = fail
    ? item.detail
    : plan
      ? 'Sent to the fleet at its start time. How long it takes is known once it runs.'
      : outcomeNote(item);
  const length = item.endMs - item.startMs;
  const live = item.live;
  const canOpen = Boolean(props.onOpenTask) && Boolean(item.live || item.completion);
  const scheduled = item.kind === 'occurrence' && item.outcome === 'scheduled' && item.occurrence;
  const showPreviewLine =
    actions.actionsOff &&
    Boolean((scheduled && (actions.startNow || actions.skip)) || (schedule && actions.setEnabled));
  return (
    <PhoneSheet
      title={whereOf(item) ? `${item.typeLabel} · ${whereOf(item)}` : item.typeLabel}
      eyebrow={robotText(item, props.labelOf)}
      onClose={props.onCloseSheet}
    >
      <dl className="nx-pcal-kv">
        <Kv
          k="When"
          v={
            length > 0 && !plan
              ? formatRange(item.startMs, item.endMs, timeZone)
              : formatZonedClock(item.startMs, timeZone)
          }
        />
        <Kv
          k="Length"
          v={
            plan
              ? 'Known once it runs'
              : length > 0
                ? `${formatDuration(length)}${item.estimated ? ' (estimate)' : ''}`
                : 'did not start'
          }
        />
        <Kv k="Status" v={<span data-testid="pcal-sheet-status">{phoneStatus(item.outcome)}</span>} fail={fail} />
        <Kv k="Type" v={CATEGORY_LABEL[item.lane]} />
        {/* Only for High: every job is Normal unless somebody chose. */}
        {item.high && <Kv k="Priority" v={<span data-testid="pcal-sheet-priority">High</span>} />}
        <Kv k="From" v={schedule ? `Schedule · ${schedule.name}` : sourceText(item.source)} />
      </dl>
      {note && <p className={`nx-pcal-note${fail ? ' is-fail' : ''}`}>{note}</p>}
      {showPreviewLine && (
        <p className="nx-pcal-note is-hold" data-testid="pcal-preview-line">
          {PREVIEW_LINE}
        </p>
      )}
      {scheduled && <OccurrenceActions occurrence={scheduled} actions={actions} nowMs={props.nowMs} timeZone={timeZone} />}
      {live && (!terminal(live) || isRunning(item.outcome)) && props.renderTaskCancel?.(live)}
      <div className="nx-pcal-actions">
        {pauseTarget && pauseTarget.enabled && (
          <PauseButton
            schedule={pauseTarget}
            schedules={props.schedules}
            actions={actions}
            nowMs={props.nowMs}
            timeZone={timeZone}
          />
        )}
        {canOpen && (
          <button
            type="button"
            className="nx-pcal-btn"
            onClick={() => {
              props.onCloseSheet();
              props.onOpenTask!(item);
            }}
            data-testid="pcal-open-task"
          >
            Open task
          </button>
        )}
      </div>
      <ActionStatus status={actions.status} />
    </PhoneSheet>
  );
}

function ScheduleSheet(
  props: PhoneCalendarProps & {
    row: ScheduleRow;
  },
) {
  const { row, actions, timeZone, nowMs } = props;
  const s = row.schedule;
  const repeats = repeatsLine(row.pattern, row.rule, row.span);
  return (
    <PhoneSheet title={s.name} eyebrow="Repeating schedule" onClose={props.onCloseSheet}>
      <dl className="nx-pcal-kv">
        {/* The end is the Runs line's to say, with its weekday. */}
        <Kv k={repeats.k} v={repeats.v} />
        {row.span && <Kv k="Runs" v={<span data-testid="pcal-sheet-span">{row.span}</span>} />}
        <Kv k="Robot" v={row.robots} />
        <Kv k="Type" v={CATEGORY_LABEL[row.lane]} />
        {row.active && (
          <Kv
            k="Next"
            v={row.next ? nextRunText(row.next.start_ms, nowMs, timeZone) : 'Not in the next two days'}
          />
        )}
        <Kv k="Status" v={row.active ? 'Active' : 'Paused'} />
        {/* Who set it up, by name (api-server 0.6.54): "You", a name, an
            address, or "A team member" -- never a raw id. The phone has
            no Edit; who CHANGED it is on the Audit screen. */}
        <Kv
          k="Set up by"
          v={<span data-testid="pcal-sheet-set-up-by">{standaloneLabel(creatorLabel(s, props.mySub))}</span>}
        />
      </dl>
      {actions.actionsOff && (actions.setEnabled || actions.startNow) && (
        <p className="nx-pcal-note is-hold" data-testid="pcal-preview-line">
          {PREVIEW_LINE}
        </p>
      )}
      {row.next && (
        <>
          <h3 className="nx-pcal-h">Next run</h3>
          <OccurrenceActions occurrence={row.next} actions={actions} nowMs={nowMs} timeZone={timeZone} />
        </>
      )}
      <div className="nx-pcal-actions">
        {!row.ended && (
          <PauseButton
            schedule={s}
            schedules={props.schedules}
            actions={actions}
            nowMs={nowMs}
            timeZone={timeZone}
          />
        )}
      </div>
      <ActionStatus status={actions.status} />
    </PhoneSheet>
  );
}
